import getCdpsAbi from '../../../blockchain/abi/get-cdps.json';
import {
  CDP_MANAGER,
  GET_CDPS,
} from '../../../blockchain/addresses/polkadot.json';
import { ethers } from 'ethers';
import { getProxyAddress, initialContract } from 'utils/contract/web3';
import {
  cdpManagerIlks$,
  cdpManagerOwner$,
  cdpManagerUrns$,
} from 'utils/contract/cdpManager/cdpManager';
import { createController$ } from 'utils/contract/proxy/controller';
import { ilkToToken } from 'utils/contract/common-function';
import { vatGem$, vatUrns$ } from 'utils/contract/ilks/vat';
import { createOraclePriceData$ } from 'utils/contract/pip/oracle';
import { createIlkData$ } from 'utils/contract/ilks';
import { one, zero } from 'constants/zero';
import { cacheApi, HOUR, SECONDS_PER_YEAR } from 'constants/variables';
import { BigNumber } from 'bignumber.js';
import { ReturnedEvent, Vault, VaultEvent, VaultSummary } from 'types/Ilk';
import { flatten, groupBy, mapValues, memoize, pickBy } from 'lodash';
import { gql, GraphQLClient } from 'graphql-request';
import { useEffect, useState } from 'react';

// const { CDP_MANAGER, GET_CDPS } = MULTICHAIN_SETUP.polka.address

export const fetchAllLoansByAddress = async (
  address: string,
  proxy?: string,
) => {
  if (!proxy) {
    proxy = await getProxyAddress(address);
  }
  //console.log({ proxy });
  if (proxy === ethers.constants.AddressZero) {
    return [];
  }

  const contract = initialContract(getCdpsAbi, GET_CDPS);
  const cdp = await contract.methods.getCdpsAsc(CDP_MANAGER, proxy).call();

  const { ids } = cdp;

  const res = await Promise.all(
    ids.map(async loadID => await fetchLoanById(loadID)),
  );

  return res;
};

function getTotalCollateralPrice(vaults: Vault[]) {
  return vaults.reduce(
    (total, vault) => total.plus(vault.lockedCollateralUSD),
    new BigNumber(0),
  );
}

function getTotalDaiDebt(vaults: Vault[]) {
  return vaults.reduce(
    (total, vault) => total.plus(vault.debt),
    new BigNumber(0),
  );
}

function getAssetRatio(vaults: Vault[], totalLocked: BigNumber) {
  const vaultsByToken = groupBy(vaults, vault => vault.token);
  return mapValues(vaultsByToken, vaults =>
    getTotalCollateralPrice(vaults).div(totalLocked),
  );
}

export function getLoansSummary(vaults: Vault[]): VaultSummary {
  const totalCollateralPrice = getTotalCollateralPrice(vaults);
  return {
    numberOfVaults: vaults.length,
    vaultsAtRisk: vaults.reduce(
      (total, vault) => (vault.atRiskLevelDanger ? total + 1 : total),
      0,
    ),
    totalCollateralPrice,
    totalDaiDebt: getTotalDaiDebt(vaults),
    depositedAssetRatio: getAssetRatio(vaults, totalCollateralPrice),
    statistic: getAllAssets(vaults),
  };
}

export function getAllAssets(vaults: Vault[]) {
  const assets: { [X: string]: number } = vaults
    .map(item => item.token)
    .reduce((acc, curr) => ((acc[curr] = (acc[curr] || 0) + 1), acc), {});
  const entries = Object.entries(assets).map(([key, value]) => [key, value]);
  return entries;
}

export const fetchLoanById = async (loanId: string) => {
  try {
    const id = parseInt(loanId);

    const loanDetail = await Promise.all([
      cdpManagerUrns$(id),
      cdpManagerIlks$(id),
      cdpManagerOwner$(id),
      createController$(id),
    ])
      .then(([urnAddress, ilk, owner, controller]) => {
        //console.log(ilk);
        const token = ilkToToken(ilk);
        return Promise.all([
          vatUrns$({ ilks: ilk, urnAddress: urnAddress }),
          vatGem$({ ilks: ilk, urnAddress: urnAddress }),
          createOraclePriceData$(token),
          createIlkData$(ilk),
          Promise.resolve(urnAddress),
          Promise.resolve(ilk),
          Promise.resolve(owner),
          Promise.resolve(controller),
          Promise.resolve(token),
        ]);
      })
      .then(res => {
        //console.log(res);
        const [
          { collateral, normalizedDebt },
          unlockedCollateral,
          { currentPrice, nextPrice },
          {
            debtScalingFactor,
            liquidationRatio,
            collateralizationDangerThreshold,
            collateralizationWarningThreshold,
            stabilityFee,
            ilkDebtAvailable,
          },
          urnAddress,
          ilk,
          owner,
          controller,
          token,
        ] = res;
        const collateralUSD = collateral.times(currentPrice);
        const collateralUSDAtNextPrice = nextPrice
          ? collateral.times(nextPrice)
          : currentPrice;

        const debt = debtScalingFactor.times(normalizedDebt);

        const debtOffset = !debt.isZero()
          ? debt
              .times(one.plus(stabilityFee.div(SECONDS_PER_YEAR)).pow(HOUR * 5))
              .minus(debt)
              .dp(18, BigNumber.ROUND_DOWN)
          : new BigNumber('1e-18');

        const backingCollateral = debt
          .times(liquidationRatio)
          .div(currentPrice);

        const backingCollateralAtNextPrice = debt
          .times(liquidationRatio)
          .div(nextPrice);
        const backingCollateralUSD = backingCollateral.times(currentPrice);
        const backingCollateralUSDAtNextPrice =
          backingCollateralAtNextPrice.times(nextPrice);

        const freeCollateral = backingCollateral.gte(collateral)
          ? zero
          : collateral.minus(backingCollateral);
        const freeCollateralAtNextPrice = backingCollateralAtNextPrice.gte(
          collateral,
        )
          ? zero
          : collateral.minus(backingCollateralAtNextPrice);

        const freeCollateralUSD = freeCollateral.times(currentPrice);
        const freeCollateralUSDAtNextPrice =
          freeCollateralAtNextPrice.times(nextPrice);

        const collateralizationRatio = debt.isZero()
          ? zero
          : collateralUSD.div(debt);
        const collateralizationRatioAtNextPrice = debt.isZero()
          ? zero
          : collateralUSDAtNextPrice.div(debt);

        const maxAvailableDebt = collateralUSD.div(liquidationRatio);
        const maxAvailableDebtAtNextPrice =
          collateralUSDAtNextPrice.div(liquidationRatio);

        const availableDebt = debt.lt(collateralUSD.div(liquidationRatio))
          ? maxAvailableDebt.minus(debt)
          : zero;

        const availableDebtAtNextPrice = debt.lt(maxAvailableDebtAtNextPrice)
          ? maxAvailableDebtAtNextPrice.minus(debt)
          : zero;

        const liquidationPrice = collateral.eq(zero)
          ? zero
          : debt.times(liquidationRatio).div(collateral);

        const daiYieldFromLockedCollateral = availableDebt.lt(ilkDebtAvailable)
          ? availableDebt
          : ilkDebtAvailable.gt(zero)
          ? ilkDebtAvailable
          : zero;
        const atRiskLevelWarning =
          collateralizationRatio.gte(collateralizationDangerThreshold) &&
          collateralizationRatio.lt(collateralizationWarningThreshold);

        const atRiskLevelDanger =
          collateralizationRatio.gte(liquidationRatio) &&
          collateralizationRatio.lt(collateralizationDangerThreshold);

        const underCollateralized =
          !collateralizationRatio.isZero() &&
          collateralizationRatio.lt(liquidationRatio);

        const atRiskLevelWarningAtNextPrice =
          collateralizationRatioAtNextPrice.gte(
            collateralizationDangerThreshold,
          ) &&
          collateralizationRatioAtNextPrice.lt(
            collateralizationWarningThreshold,
          );

        const atRiskLevelDangerAtNextPrice =
          collateralizationRatioAtNextPrice.gte(liquidationRatio) &&
          collateralizationRatioAtNextPrice.lt(
            collateralizationDangerThreshold,
          );

        const underCollateralizedAtNextPrice =
          !collateralizationRatioAtNextPrice.isZero() &&
          collateralizationRatioAtNextPrice.lt(liquidationRatio);

        return {
          id: new BigNumber(loanId),
          ilk,
          token,
          address: urnAddress,
          owner,
          controller,
          lockedCollateral: collateral,
          lockedCollateralUSD: collateralUSD,
          backingCollateral,
          backingCollateralUSD,
          freeCollateral,
          freeCollateralUSD,
          lockedCollateralUSDAtNextPrice: collateralUSDAtNextPrice,
          backingCollateralAtNextPrice,
          backingCollateralUSDAtNextPrice,
          freeCollateralAtNextPrice,
          freeCollateralUSDAtNextPrice,
          normalizedDebt,
          debt,
          debtOffset,
          availableDebt,
          availableDebtAtNextPrice,
          unlockedCollateral,
          collateralizationRatio,
          collateralizationRatioAtNextPrice,
          liquidationPrice,
          daiYieldFromLockedCollateral,

          atRiskLevelWarning,
          atRiskLevelDanger,
          underCollateralized,
          atRiskLevelWarningAtNextPrice,
          atRiskLevelDangerAtNextPrice,
          underCollateralizedAtNextPrice,
        };
      });

    //console.log(loanDetail);

    return loanDetail;
  } catch (err) {
    //console.log('fetch detail error: ', err.message);
  }
};

const query = gql`
  query vaultEvents($urn: String) {
    allVaultEvents(
      filter: { urn: { equalTo: $urn }, kind: { notEqualTo: "TAKE" } }
      orderBy: [TIMESTAMP_DESC, LOG_INDEX_DESC]
    ) {
      nodes {
        kind
        collateralAmount
        daiAmount
        vaultCreator
        cdpId
        transferFrom
        transferTo
        collateralTaken
        coveredDebt
        remainingCollateral
        timestamp
        id
        urn
        hash
        logIndex
        auctionId
      }
    }
  }
`;

function parseBigNumbersFields(
  event: Partial<ReturnedEvent>,
  token: string,
): VaultEvent {
  const bigNumberFields = [
    'collateralAmount',
    'daiAmount',
    'collateralTaken',
    'coveredDebt',
    'remainingCollateral',
  ];
  return Object.entries(event).reduce(
    (acc, [key, value]) =>
      bigNumberFields.includes(key) && value != null
        ? { ...acc, [key]: new BigNumber(value) }
        : { ...acc, [key]: value },
    { token: token },
  ) as VaultEvent;
}

function splitEvents(event: VaultEvent): VaultEvent | VaultEvent[] {
  if (event.kind === 'DEPOSIT-GENERATE') {
    return [
      {
        ...event,
        id: `${event.id}_a`,
        kind: 'GENERATE',
      },
      {
        ...event,
        kind: 'DEPOSIT',
      },
    ];
  }
  if (event.kind === 'WITHDRAW-PAYBACK') {
    return [
      {
        ...event,
        kind: 'PAYBACK',
      },
      {
        ...event,
        id: `${event.id}_a`,
        kind: 'WITHDRAW',
      },
    ];
  }
  return event;
}

export type VaultHistoryEvent = VaultEvent & {
  token: string;
  etherscan?: {
    url: string;
    apiUrl: string;
    apiKey: string;
  };
};

function fetchWithOperationId(url: string, options?: RequestInit) {
  const operationNameRegex = /query (?<operationName>[a-zA-Z0-9]+)\(/gm;

  const body = typeof options?.body === 'string' ? options?.body : '';
  const parsedBody: { query: string } = JSON.parse(body);
  const result = operationNameRegex.exec(parsedBody.query);
  const operationName = result ? result.groups?.operationName : undefined;

  return fetch(url, {
    ...options,
    body: JSON.stringify({ ...parsedBody, operationName }),
  });
}

const getLoanHistory = async (
  client: GraphQLClient,
  urn: string,
): Promise<ReturnedEvent[]> => {
  try {
    const data = await client.request(query, { urn: urn.toLowerCase() });

    return data.allVaultEvents.nodes as ReturnedEvent[];
  } catch (err) {
    //console.log(err.message);
    return [];
  }
};

export const useLoanHistory = (urns?: string, loan?: string) => {
  const [history, setHistory] = useState<VaultEvent[]>([]);

  const fetchLoanHistory = async () => {
    try {
      if (!urns || !loan) {
        return [];
      }

      const ilk = await cdpManagerIlks$(parseInt(loan));
      const token = ilkToToken(ilk);

      const makeClient = memoize(
        (url: string) =>
          new GraphQLClient(url, { fetch: fetchWithOperationId }),
      );

      void getLoanHistory(makeClient(cacheApi as string), urns).then(res => {
        const his = flatten(
          res
            .map(returnedEvent =>
              pickBy(returnedEvent, value => value !== null),
            )
            .map(event => parseBigNumbersFields(event, token))
            .map(splitEvents),
        );
        //console.log(his);
        setHistory(his);
      });
    } catch (err) {}
  };

  useEffect(() => {
    fetchLoanHistory();
  }, [urns, loan]);

  return history;
};
